import axios from 'axios';
import { createAsyncThunk, createSlice, isFulfilled, isPending, isRejected } from '@reduxjs/toolkit';
import { userIamUrl } from '@/config/user-iam';
import { defaultValue } from '../model/permission.model';
import { serializeAxiosError, resolveAsyncThunkErrorMessage } from './reducer.utils';
import { fromIamPermission, normalizeIamPermissionsList, readIamPermissionsPage, toIamPermissionPayload, } from '@/shared/utils/iam-permission-api';
/** @deprecated use {@link normalizeIamPermissionsList} */
export function normalizePublicUserPermissionsResponse(data) {
    return normalizeIamPermissionsList(data);
}
const initialState = {
    loading: false,
    errorMessage: null,
    permissions: [],
    /** IAM permission list (`user/api/permissions`) — use for PickLists (role/company forms). */
    publicUserPermissions: [],
    publicUserPermissionsLoading: false,
    permission: defaultValue,
    updating: false,
    updateSuccess: false,
    totalItems: 0,
};
const permissionsApiUrl = () => userIamUrl('/user/api/permissions');
export const getPermissions = createAsyncThunk('permissionManagement/fetch_permissions', async ({ page, size, sort, filter }) => {
    const cacheBuster = `cacheBuster=${Date.now()}`;
    const filterSuffix = filter ? `&action.equals=${encodeURIComponent(filter)}` : '';
    const response = await axios.get(`${permissionsApiUrl()}?${cacheBuster}${filterSuffix}`);
    const { rows, total } = readIamPermissionsPage(response.data, response.headers, page, size, sort, filter);
    return {
        ...response,
        data: rows,
        headers: { ...response.headers, 'x-total-count': String(total) },
    };
}, { serializeError: serializeAxiosError });
/** Loads IAM permissions for role/company PickLists (Bearer via axios interceptors). */
export const getPublicUserPermissions = createAsyncThunk('permissionManagement/fetch_public_user_permissions', async () => axios.get(permissionsApiUrl(), { params: { cacheBuster: Date.now() } }), { serializeError: serializeAxiosError });
export const getPermission = createAsyncThunk('permissionManagement/fetch_permission', async (id) => {
    const response = await axios.get(`${permissionsApiUrl()}/${id}`);
    return { ...response, data: fromIamPermission(response.data) };
}, { serializeError: serializeAxiosError });
export const createPermission = createAsyncThunk('permissionManagement/create_permission', async (entity) => {
    const payload = toIamPermissionPayload(entity);
    const response = await axios.post(permissionsApiUrl(), payload);
    return { ...response, data: fromIamPermission(response.data) };
}, { serializeError: serializeAxiosError });
export const updatePermission = createAsyncThunk('permissionManagement/update_permission', async (entity) => {
    const payload = toIamPermissionPayload(entity);
    const id = entity.id;
    const url = id != null && id !== '' ? `${permissionsApiUrl()}/${id}` : permissionsApiUrl();
    const response = await axios.put(url, payload);
    return { ...response, data: fromIamPermission(response.data) };
}, { serializeError: serializeAxiosError });
export const deletePermission = createAsyncThunk('permissionManagement/delete_permission', async (id) => {
    const response = await axios.delete(`${permissionsApiUrl()}/${id}`);
    return response;
}, { serializeError: serializeAxiosError });
export const PermissionManagementSlice = createSlice({
    name: 'permissionManagement',
    initialState: initialState,
    reducers: {
        reset() {
            return initialState;
        },
    },
    extraReducers(builder) {
        builder
            .addCase(getPermission.fulfilled, (state, action) => {
            state.loading = false;
            state.permission = action.payload.data;
        })
            .addCase(deletePermission.fulfilled, state => {
            state.updating = false;
            state.updateSuccess = true;
            state.permission = defaultValue;
        })
            .addCase(getPublicUserPermissions.fulfilled, (state, action) => {
            state.publicUserPermissionsLoading = false;
            state.publicUserPermissions = normalizeIamPermissionsList(action.payload?.data);
        })
            .addMatcher(isFulfilled(getPermissions), (state, action) => {
            state.loading = false;
            state.permissions = action.payload.data;
            state.totalItems = parseInt(action.payload.headers['x-total-count'], 10);
        })
            .addMatcher(isFulfilled(createPermission, updatePermission), (state, action) => {
            state.updating = false;
            state.loading = false;
            state.updateSuccess = true;
            state.permission = action.payload.data;
        })
            .addMatcher(isPending(getPublicUserPermissions), state => {
            state.publicUserPermissionsLoading = true;
            state.errorMessage = null;
        })
            .addMatcher(isRejected(getPublicUserPermissions), (state, action) => {
            state.publicUserPermissionsLoading = false;
            state.errorMessage = resolveAsyncThunkErrorMessage(action.error);
        })
            .addMatcher(isPending(getPermissions, getPermission), state => {
            state.errorMessage = null;
            state.updateSuccess = false;
            state.loading = true;
        })
            .addMatcher(isPending(createPermission, updatePermission, deletePermission), state => {
            state.errorMessage = null;
            state.updateSuccess = false;
            state.updating = true;
        })
            .addMatcher(isRejected(getPermissions, getPermission, createPermission, updatePermission, deletePermission), (state, action) => {
            state.loading = false;
            state.updating = false;
            state.updateSuccess = false;
            state.errorMessage = resolveAsyncThunkErrorMessage(action.error);
        });
    },
});
export const { reset } = PermissionManagementSlice.actions;
export default PermissionManagementSlice.reducer;
